import { SqButton } from "@/components/squircle-ui/button";
import { RiAddLargeLine } from "@remixicon/react";
import Lottie from "lottie-react";
import emptyReportAnimation from "@/assets/lottieAnimations/empty_report.json";
import { CreativeReportListItem } from "../../types";
import { CreativeSectionConfiguration, TimeInterval } from "@/types";
import { getDefaultColumnChoices } from "../../utils/getDefaultColumnChoices";
import { useGlobalStore } from "@/GlobalStore";
import { getPredefinedRanges, formatForAPI } from "@/utils/dateUtils";
import { useState } from "react";
import { createCreativeReport } from "../../apiService";
import { IconLoader2 } from "@tabler/icons-react";
import { TZDate } from "@date-fns/tz";
import {
  accountDefaultDateKeyToTimeInterval,
  getAccountDefaultDateKeyFromAccount,
} from "@/utils/defaultDateUtils";

const NoReports = ({
  allowedDateRange,
}: {
  allowedDateRange?: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  };
}) => {
  const [isCreatingReport, setIsCreatingReport] = useState<boolean>(false);
  const {
    selectedAccount,
    creativeReportList,
    setCreativeReportList,
    setSelectedCreativeReport,
    dataConfigurations,
  } = useGlobalStore();

  const createDefaultReport = (): Omit<CreativeReportListItem, "id"> & {
    section_configuration: CreativeSectionConfiguration;
  } => {
    const timezone = selectedAccount?.time_zone ?? "UTC";
    const defaultKey = getAccountDefaultDateKeyFromAccount(selectedAccount);
    const label = accountDefaultDateKeyToTimeInterval(defaultKey);
    const presets = getPredefinedRanges(timezone);
    const presetRange = presets[label] ?? presets[TimeInterval.LAST_7_DAYS];
    const dateRange = presetRange ?? {
      from: new Date(),
      to: new Date(),
    };
    return {
      title: `Report ${new Date()
        .toLocaleString("en-GB", {
          timeZone: selectedAccount?.time_zone ?? "UTC",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-")}`,
      description: "",
      metadata: {
        date: {
          start_date: formatForAPI(dateRange.from, timezone),
          end_date: formatForAPI(dateRange.to, timezone),
          label,
        },
      },
      account: selectedAccount?.id,
      section_configuration: {
        dropdowns: {
          group_by:
            dataConfigurations?.dropdowns?.find((d) => d.key === "group_by")?.default ??
            "ad_name",
        },
        column_choices: getDefaultColumnChoices(dataConfigurations),
      },
    };
  };

  const handleAddReport = async () => {
    if (!selectedAccount?.id) return;
    try {
      setIsCreatingReport(true);
      const res = await createCreativeReport(selectedAccount?.id, createDefaultReport());
      setCreativeReportList([res.report, ...creativeReportList]);
      setSelectedCreativeReport(res.report);
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingReport(false);
    }
  };

  return (
    <div className="w-full h-dvh flex flex-col items-center justify-center">
      <Lottie
        animationData={emptyReportAnimation}
        loop={true}
        autoplay={true}
        style={{ width: "100%", height: "132px" }}
      />
      <div className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-center text-[#334478] mt-3">
        Your report shelf is empty
      </div>
      <p className="font-normal text-[12px] leading-[19.16px] tracking-[0.02em] text-center text-[#67749B] mt-[6px]">
        Add your first report using the button below.
      </p>
      <SqButton
        variant={"outline"}
        size={"sm"}
        className="mt-3 !bg-transparent"
        shadow={"sm"}
        radius={6}
        onClick={() => void handleAddReport()}
      >
        {isCreatingReport ? (
          <IconLoader2
            size={14}
            className="shrink-0 animate-spin text-brand-primary-400"
          />
        ) : (
          <RiAddLargeLine size={14} color="#9aa3bd" className="shrink-0" />
        )}
        <span>Create Report</span>
      </SqButton>
    </div>
  );
};

export default NoReports;
